import React from 'react';
import { ArrowLeft, Shield, Lock, Eye, UserCheck, Database, Globe } from 'lucide-react';
import { Link } from 'react-router-dom';

function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-4xl mx-auto px-4">
        {/* Back Button */}
        <Link to="/" className="inline-flex items-center gap-2 text-orange-600 hover:text-orange-700 mb-6 transition-colors">
          <ArrowLeft className="w-4 h-4" />
          <span className="text-sm font-semibold">Back to Home</span>
        </Link>

        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm p-8 mb-6">
          <div className="flex items-center gap-3 mb-4">
            <Shield className="w-8 h-8 text-orange-600" />
            <h1 className="text-3xl font-bold text-slate-900">Privacy Policy</h1>
          </div>
          <p className="text-slate-600 text-sm">Last Updated: January 2026</p>
        </div>

        {/* Content */}
        <div className="bg-white rounded-lg shadow-sm p-8 space-y-8">
          
          {/* Introduction */}
          <section>
            <h2 className="text-xl font-bold text-slate-900 mb-4">Introduction</h2>
            <p className="text-slate-700 leading-relaxed">
              BrightBazaar.in (operated by BrightPlanet Ventures) respects your privacy and is committed 
              to protecting your personal information. This Privacy Policy explains how we collect, use, 
              disclose, and safeguard your information when you visit our website and use our services.
            </p>
          </section>

          {/* Information We Collect */}
          <section>
            <div className="flex items-start gap-3 mb-4">
              <Database className="w-6 h-6 text-orange-600 mt-1" />
              <div>
                <h2 className="text-xl font-bold text-slate-900 mb-3">Information We Collect</h2>
                
                <div className="space-y-4">
                  <div>
                    <h3 className="font-semibold text-slate-900 mb-2">Personal Information</h3>
                    <p className="text-slate-700 text-sm mb-2">
                      We collect information that you provide directly to us, including:
                    </p>
                    <ul className="space-y-1 text-slate-700 text-sm ml-4">
                      <li>• Name, email address, phone number</li>
                      <li>• Shipping and billing addresses</li>
                      <li>• Payment information (processed securely through payment gateways)</li>
                      <li>• Account credentials (username and password)</li>
                      <li>• Customer ID and card number (for loyalty program)</li>
                      <li>• Order history and preferences</li>
                    </ul>
                  </div>

                  <div>
                    <h3 className="font-semibold text-slate-900 mb-2">Automatically Collected Information</h3>
                    <ul className="space-y-1 text-slate-700 text-sm ml-4">
                      <li>• Device information (IP address, browser type, operating system)</li>
                      <li>• Usage data (pages visited, time spent, click patterns)</li>
                      <li>• Cookies and similar tracking technologies</li>
                      <li>• Location data (with your permission)</li>
                    </ul>
                  </div>

                  <div>
                    <h3 className="font-semibold text-slate-900 mb-2">Information from Third Parties</h3>
                    <ul className="space-y-1 text-slate-700 text-sm ml-4">
                      <li>• Social media platforms (if you choose to link your account)</li>
                      <li>• Payment processors and financial institutions</li>
                      <li>• Delivery and logistics partners</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* How We Use Information */}
          <section>
            <div className="flex items-start gap-3 mb-4">
              <Eye className="w-6 h-6 text-orange-600 mt-1" />
              <div>
                <h2 className="text-xl font-bold text-slate-900 mb-3">How We Use Your Information</h2>
                <p className="text-slate-700 mb-3">We use the information we collect to:</p>
                <ul className="space-y-2 text-slate-700">
                  <li className="flex items-start gap-2">
                    <span className="text-orange-600 mt-1">✓</span>
                    <span>Process and fulfill your orders and payments</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-orange-600 mt-1">✓</span>
                    <span>Manage your account and provide customer support</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-orange-600 mt-1">✓</span>
                    <span>Send order confirmations, shipping updates, and invoices</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-orange-600 mt-1">✓</span>
                    <span>Communicate promotional offers and newsletters (with your consent)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-orange-600 mt-1">✓</span>
                    <span>Improve our website, products, and services</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-orange-600 mt-1">✓</span>
                    <span>Detect, prevent, and address fraud and security issues</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-orange-600 mt-1">✓</span>
                    <span>Comply with legal obligations and enforce our terms</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-orange-600 mt-1">✓</span>
                    <span>Personalize your shopping experience and recommend products</span>
                  </li>
                </ul>
              </div>
            </div>
          </section>

          {/* Information Sharing */}
          <section>
            <div className="flex items-start gap-3 mb-4">
              <Globe className="w-6 h-6 text-orange-600 mt-1" />
              <div>
                <h2 className="text-xl font-bold text-slate-900 mb-3">How We Share Your Information</h2>
                <p className="text-slate-700 mb-3">
                  We do not sell your personal information. We may share your information with:
                </p>
                <ul className="space-y-3">
                  <li className="bg-gray-50 rounded-lg p-4">
                    <h3 className="font-semibold text-slate-900 text-sm mb-1">Service Providers</h3>
                    <p className="text-slate-700 text-sm">
                      Third-party companies that help us operate our business (payment processors, 
                      shipping companies, email service providers)
                    </p>
                  </li>
                  <li className="bg-gray-50 rounded-lg p-4">
                    <h3 className="font-semibold text-slate-900 text-sm mb-1">Business Transfers</h3>
                    <p className="text-slate-700 text-sm">
                      In connection with mergers, acquisitions, or sale of company assets
                    </p>
                  </li>
                  <li className="bg-gray-50 rounded-lg p-4">
                    <h3 className="font-semibold text-slate-900 text-sm mb-1">Legal Requirements</h3>
                    <p className="text-slate-700 text-sm">
                      When required by law or to protect our rights and safety
                    </p>
                  </li>
                  <li className="bg-gray-50 rounded-lg p-4">
                    <h3 className="font-semibold text-slate-900 text-sm mb-1">With Your Consent</h3>
                    <p className="text-slate-700 text-sm">
                      When you explicitly authorize us to share your information
                    </p>
                  </li>
                </ul>
              </div>
            </div>
          </section>

          {/* Data Security */}
          <section>
            <div className="flex items-start gap-3 mb-4">
              <Lock className="w-6 h-6 text-orange-600 mt-1" />
              <div>
                <h2 className="text-xl font-bold text-slate-900 mb-3">Data Security</h2>
                <p className="text-slate-700 leading-relaxed mb-3">
                  We implement appropriate technical and organizational security measures to protect 
                  your personal information, including:
                </p>
                <ul className="space-y-2 text-slate-700 text-sm">
                  <li>• SSL/TLS encryption for data transmission</li>
                  <li>• Secure servers with firewall protection</li>
                  <li>• Regular security audits and updates</li>
                  <li>• Restricted access to personal information</li>
                  <li>• Employee training on data protection</li>
                </ul>
                <p className="text-slate-700 leading-relaxed mt-3 text-sm">
                  However, no method of transmission over the internet is 100% secure. While we strive 
                  to protect your information, we cannot guarantee absolute security.
                </p>
              </div>
            </div>
          </section>

          {/* Your Rights */}
          <section>
            <div className="flex items-start gap-3 mb-4">
              <UserCheck className="w-6 h-6 text-orange-600 mt-1" />
              <div>
                <h2 className="text-xl font-bold text-slate-900 mb-3">Your Rights and Choices</h2>
                <p className="text-slate-700 mb-3">You have the right to:</p>
                <ul className="space-y-2 text-slate-700">
                  <li className="flex items-start gap-2">
                    <span className="text-orange-600 mt-1">→</span>
                    <span><strong>Access:</strong> Request a copy of your personal information</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-orange-600 mt-1">→</span>
                    <span><strong>Correction:</strong> Update or correct inaccurate information</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-orange-600 mt-1">→</span>
                    <span><strong>Deletion:</strong> Request deletion of your personal information</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-orange-600 mt-1">→</span>
                    <span><strong>Opt-out:</strong> Unsubscribe from marketing emails</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-orange-600 mt-1">→</span>
                    <span><strong>Data Portability:</strong> Receive your data in a structured format</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-orange-600 mt-1">→</span>
                    <span><strong>Restrict Processing:</strong> Limit how we use your information</span>
                  </li>
                </ul>
                <p className="text-slate-700 mt-4 text-sm">
                  To exercise these rights, please contact us at <strong>info@brightplanetventures.com</strong>
                </p>
              </div>
            </div>
          </section>

          {/* Cookies */}
          <section className="bg-orange-50 rounded-lg p-6">
            <h2 className="text-xl font-bold text-slate-900 mb-3">Cookies and Tracking</h2>
            <p className="text-slate-700 leading-relaxed mb-3">
              We use cookies and similar technologies to enhance your browsing experience. You can 
              manage cookie preferences through your browser settings. Note that disabling cookies 
              may affect website functionality.
            </p>
            <p className="text-slate-700 text-sm">
              Types of cookies we use: Essential cookies, Performance cookies, Functional cookies, 
              and Marketing cookies.
            </p>
          </section>

          {/* Children's Privacy */}
          <section>
            <h2 className="text-xl font-bold text-slate-900 mb-3">Children's Privacy</h2>
            <p className="text-slate-700 leading-relaxed">
              Our services are not directed to children under 18 years of age. We do not knowingly 
              collect personal information from children. If you believe we have collected information 
              from a child, please contact us immediately.
            </p>
          </section>

          {/* Changes to Policy */}
          <section>
            <h2 className="text-xl font-bold text-slate-900 mb-3">Changes to This Policy</h2>
            <p className="text-slate-700 leading-relaxed">
              We may update this Privacy Policy from time to time. We will notify you of significant 
              changes by posting the new policy on this page and updating the "Last Updated" date. 
              Your continued use of our services after changes constitutes acceptance of the updated policy.
            </p>
          </section>

          {/* Contact Section */}
          <section className="border-t pt-6">
            <h2 className="text-xl font-bold text-slate-900 mb-4">Contact Us</h2>
            <p className="text-slate-700 leading-relaxed mb-4">
              If you have questions or concerns about this Privacy Policy or our data practices, 
              please contact us:
            </p>
            <div className="space-y-2 text-sm bg-gray-50 rounded-lg p-4">
              <p className="text-slate-700"><strong>BrightPlanet Ventures</strong></p>
              <p className="text-slate-700">Mulla Complex, Somwar Pethe</p>
              <p className="text-slate-700">Near MalaPrabha Hospital</p>
              <p className="text-slate-700">Bailhongal - 591102, Karnataka, India</p>
              <p className="text-slate-700 mt-3"><strong>Phone:</strong> +91 73532 97211</p>
              <p className="text-slate-700"><strong>Email:</strong> info@brightplanetventures.com</p>
              <p className="text-slate-700"><strong>GST:</strong> 29AAKCD7090A1ZL</p>
            </div>
          </section>

        </div>
      </div>
    </div>
  );
}

export default PrivacyPolicy;
